//-------------------------------------------------------------------------------------------------- Imports
import { getAssetsConfigData } from "../stores/slices/assetsConfigSlice";
import { getConfigurableServiceState } from "../stores/slices/configurableServicesSlice";
import { getMobilePhoneData } from "../stores/slices/mobilePhoneSlice";
import { getTrackingMegamovilData } from "../stores/slices/megamovilTrackingSlice";
import type { ConfigurableService } from "../types/common";

//-------------------------------------------------------------------------------------------------- Types
export type FeatureKey =
  | "PARENTAL_CONTROL"
  | "REFERRAL_SYSTEM"
  | "MOBILE_TELEPHONY"
  | "MEGAN_CHAT"
  | "TECHNICAL_DIAGNOSTICS"
  | "MY_NETWORK"
  | "UPDATE_DATA";

export interface FeatureRule {
  select: (state: any) => any;
  isReady: (data: any) => boolean;
  isAllowed: (data: any) => boolean;
  redirectTo?: string;
  menuKeys?: string[];
}

//-------------------------------------------------------------------------------------------------- Helpers
const confServiceRule = (
  predicate: (name: string) => boolean,
  extra?: Partial<FeatureRule>,
): FeatureRule => ({
  select: (state: any) => getConfigurableServiceState(state),
  isReady: (data: any) => data.dataList.length > 0,
  isAllowed: (data: any) =>
    data.dataList.some(
      (service: ConfigurableService) => predicate(service.name) && service.enabled,
    ),
  ...extra,
});

//-------------------------------------------------------------------------------------------------- Registry
export const FEATURE_RULES: Record<FeatureKey, FeatureRule> = {
  PARENTAL_CONTROL: confServiceRule(
    (name) => name === "ACTIVATE_PARENTAL_CONTROL_MENU",
    { menuKeys: ["/parental-control"] },
  ),
  REFERRAL_SYSTEM: {
    select: (state: any) => getAssetsConfigData(state),
    isReady: (data: any) => data.clientId !== "",
    isAllowed: (data: any) => data.showReferidos,
    menuKeys: ["/referral-system"],
  },
  MOBILE_TELEPHONY: {
    select: (state: any) => ({
      mobile: getMobilePhoneData(state),
      tracking: getTrackingMegamovilData(state),
    }),
    isReady: (data: any) =>
      data.mobile.clientId !== "" && data.tracking.clientId !== "",
    isAllowed: (data: any) => data.mobile.hasCellPhone || data.tracking.status,
    menuKeys: ["/mobile-telephony"],
  },
  MEGAN_CHAT: confServiceRule(
    (name) => name === "ACTIVATE_AI_SUPPORT_MEGAN",
  ),
  TECHNICAL_DIAGNOSTICS: confServiceRule(
    (name) => name === "ACTIVATE_TECHNICAL_DIAGNOSTICS",
  ),
  MY_NETWORK: confServiceRule((name) =>
    name.startsWith("DAF_ACTIVATION_MY_NETWORK"),
  ),
  UPDATE_DATA: confServiceRule((name) => name.startsWith("DAF_UPDATE_DATA_"), {
    redirectTo: "/my-data",
  }),
};